import { useEffect, useRef } from 'react';
import { Group, Badge, Loader, Text } from '@mantine/core';
import { useJobLlmRequests } from '../../api/llmRequests';

interface GenerationProgressIndicatorProps {
  jobId: string;
  onGenerationComplete?: () => void;
}

export function GenerationProgressIndicator({ jobId, onGenerationComplete }: GenerationProgressIndicatorProps) {
  // Only fetch active requests (queued or processing)
  const { data: allRequests = [], refetch } = useJobLlmRequests(jobId);
  const previousStatusRef = useRef<string | null>(null);

  // Find any active request (queued or processing)
  const activeRequest = allRequests.find((req) => req.status === 'queued' || req.status === 'processing');

  // Find any completed request
  const completedRequest = allRequests.find((req) => req.status === 'completed');

  // Detect when generation completes
  useEffect(() => {
    const currentStatus = activeRequest?.status || completedRequest?.status;

    // If we had an active request before and now we don't, but there's a completed one
    if (previousStatusRef.current && (previousStatusRef.current === 'processing' || previousStatusRef.current === 'queued') &&
        completedRequest && !activeRequest) {
      // Generation just completed!
      onGenerationComplete?.();
    }

    previousStatusRef.current = currentStatus || null;
  }, [activeRequest, completedRequest, onGenerationComplete]);

  // Set up polling only when there's an active request
  useEffect(() => {
    if (!activeRequest) return;

    // Poll every 5 seconds while there's an active request
    const interval = setInterval(() => {
      refetch();
    }, 5000);

    return () => clearInterval(interval);
  }, [activeRequest, refetch]);

  if (!activeRequest) {
    return null;
  }

  const isProcessing = activeRequest.status === 'processing';

  return (
    <Group gap="sm" align="center">
      <Loader size="xs" />
      <Badge
        variant="filled"
        color={isProcessing ? 'blue' : 'gray'}
        leftSection={isProcessing ? '⏳' : '📋'}
      >
        {isProcessing ? 'Generating resume...' : 'Queued...'}
      </Badge>
      <Text size="sm" c="dimmed">
        {isProcessing
          ? 'This may take a moment. Check the Job Resumes tab for updates.'
          : 'Waiting to start generation...'}
      </Text>
    </Group>
  );
}
